import { createFileRoute, Link, useNavigate } from "@tanstack/react-router";
import { Check, X, Crown, Award } from "lucide-react";
import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { Button } from "@/components/ui/button";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import { LaunchCounter } from "@/components/launch-counter";
import { useStripeCheckout } from "@/hooks/use-stripe-checkout";
import { useAuth } from "@/lib/use-auth";
import { useSubscription } from "@/lib/use-subscription";
import { PaymentTestModeBanner } from "@/components/payment-test-mode-banner";
import { isPaymentsConfigured } from "@/lib/stripe";
import { toast } from "sonner";

const FAQS: { q: string; a: string }[] = [
  { q: "Devo inserire la carta di credito per l'offerta di lancio?", a: "No. I primi 100 chef ricevono 30 giorni di Premium Silver completamente gratis, senza carta di credito. Allo scadere il profilo torna al piano Gratuito, senza addebiti." },
  { q: "Cosa succede quando finiscono i 30 giorni?", a: "Torni al piano Gratuito. Mantieni per sempre il badge Founder e decidi tu se passare a Silver o Gold a pagamento." },
  { q: "Posso disdire in qualsiasi momento?", a: "Sì. Puoi disdire Premium quando vuoi, direttamente dalla dashboard." },
  { q: "Come ottengo il badge Chef Verificato?", a: "Dalla dashboard puoi caricare carta d'identità, P.IVA, attestato HACCP e diplomi. Il nostro team li controlla e attiva il badge in 24-48 ore." },
  { q: "Qual è la differenza tra Silver e Gold?", a: "Con Silver appari più in alto nei risultati, mostri più foto del tuo lavoro e ti fai contattare via WhatsApp. Con Gold sei tra i primi in assoluto: massima visibilità, presenza in homepage e galleria ancora più ricca." },
  { q: "Premium garantisce più richieste?", a: "Premium ti fa vedere prima e più spesso. Le richieste arrivano poi quando il profilo convince: foto curate, descrizione chiara e recensioni reali fanno la differenza." },
];

export const Route = createFileRoute("/premium")({
  head: () => ({
    meta: [
      { title: "ChefConnect Premium — Fatti scegliere prima" },
      { name: "description", content: "Con Premium il tuo profilo si vede di più, racconta meglio il tuo lavoro e rende più semplice farti contattare." },
      { property: "og:title", content: "ChefConnect Premium" },
      { property: "og:description", content: "Più visibilità, più clienti. Appari prima, fatti scegliere prima." },
      { property: "og:url", content: "https://chefconnectitalia.com/premium" },
      { property: "og:type", content: "website" },
      { property: "og:image", content: "https://chefconnectitalia.com/__l5e/assets-v1/a2ae4b98-4144-41f7-845e-72f224ca696b/og-home.jpg" },
      { name: "twitter:image", content: "https://chefconnectitalia.com/__l5e/assets-v1/a2ae4b98-4144-41f7-845e-72f224ca696b/og-home.jpg" },
    ],
    links: [{ rel: "canonical", href: "https://chefconnectitalia.com/premium" }],
    scripts: [
      {
        type: "application/ld+json",
        children: JSON.stringify({
          "@context": "https://schema.org",
          "@type": "FAQPage",
          mainEntity: FAQS.map((f) => ({
            "@type": "Question",
            name: f.q,
            acceptedAnswer: { "@type": "Answer", text: f.a },
          })),
        }),
      },
    ],
  }),
  component: PremiumPage,
});

type Row = { feature: string; free: boolean | string; silver: boolean | string; gold: boolean | string };
const ROWS: Row[] = [
  { feature: "Profilo visibile a tutti i clienti", free: true, silver: true, gold: true },
  { feature: "Un annuncio sempre attivo", free: "1", silver: "1", gold: "1" },
  { feature: "Mostra il tuo lavoro con più foto", free: "3 foto", silver: "5 foto", gold: "10 foto" },
  { feature: "Ricevi le richieste in dashboard", free: true, silver: true, gold: true },
  { feature: "Fatti contattare via WhatsApp con un tap", free: false, silver: true, gold: true },
  { feature: "Vedi chi visita il tuo profilo", free: false, silver: true, gold: true },
  { feature: "Badge argento e posizione evidenziata sulla mappa", free: false, silver: true, gold: false },
  { feature: "Badge oro e marker oro sulla mappa", free: false, silver: false, gold: true },
  { feature: "Appari più in alto nei risultati", free: false, silver: "Sopra ai gratuiti", gold: "Tra i primi in assoluto" },
  { feature: "Card in evidenza tra i risultati", free: false, silver: false, gold: true },
  { feature: "Profilo mostrato in homepage", free: false, silver: false, gold: true },
];

function Cell({ v }: { v: boolean | string }) {
  if (typeof v === "string") return <span className="text-sm">{v}</span>;
  return v ? <Check className="mx-auto h-5 w-5 text-gold" /> : <X className="mx-auto h-5 w-5 text-muted-foreground/40" />;
}

function PremiumPage() {
  const { user } = useAuth();
  const sub = useSubscription();
  const { openCheckout, checkoutElement, isOpen, closeCheckout } = useStripeCheckout();
  const navigate = useNavigate();
  // HOTFIX P0: senza profilo Chef il checkout fallisce silenziosamente
  // (FK subscriptions.chef_id → chefs.user_id). Pre-controllo client-side
  // per indirizzare l'utente alla creazione del profilo.
  const chefQ = useQuery({
    enabled: !!user,
    queryKey: ["my-chef-exists", user?.id],
    queryFn: async () => {
      const { data } = await supabase.from("chefs").select("user_id").eq("user_id", user!.id).maybeSingle();
      return !!data;
    },
  });

  const handleSubscribe = (priceId: string) => {
    if (!user) {
      toast.error("Accedi al tuo account per attivare Premium");
      return;
    }
    if (!isPaymentsConfigured()) {
      toast.error("Pagamenti non ancora attivi. Riprova più tardi.");
      return;
    }
    // Sprint 24.1 — gestione tier-aware: Gold non vede upgrade,
    // Silver vede solo "Passa a Gold", Free vede entrambi.
    if (sub.tier === "gold") {
      toast.info("Hai già Gold attivo sul tuo profilo");
      return;
    }
    if (sub.tier === "silver" && priceId === "chef_silver_monthly") {
      toast.info("Hai già Silver attivo sul tuo profilo");
      return;
    }
    if (chefQ.data === false) {
      toast.info("Crea prima il tuo profilo Chef: ti basta un minuto.", { duration: 5000 });
      navigate({ to: "/dashboard/profilo" } as never);
      return;
    }
    openCheckout({ priceId });
  };

  return (
    <>
      <PaymentTestModeBanner />
      <section className="container mx-auto max-w-5xl px-4 py-16 text-center sm:py-24">
        <span className="inline-flex items-center gap-2 rounded-full border border-gold/40 bg-gold-muted/30 px-3 py-1 text-xs font-medium">
          <Crown className="h-3.5 w-3.5 text-gold" /> ChefConnect Premium
        </span>
        <h1 className="mt-5 font-display text-5xl sm:text-6xl">Più visibilità,<br /><span className="text-gold italic">più opportunità.</span></h1>
        <p className="mx-auto mt-5 max-w-2xl text-muted-foreground">
          Premium non è "Gold = più bravo". È un set di strumenti per farti vedere prima,
          raccontare meglio il tuo lavoro e farti contattare più facilmente.
          La qualità resta la tua: noi ti diamo lo spazio per mostrarla.
        </p>
        <div className="mx-auto mt-8 max-w-md">
          <LaunchCounter variant="block" />
        </div>
      </section>

      <section className="container mx-auto max-w-5xl px-4 pb-20">
        <div className="overflow-x-auto rounded-2xl border border-border">
          <table className="w-full min-w-[560px] text-left">
            <thead className="bg-secondary/60">
              <tr>
                <th className="px-4 py-4 font-medium">Cosa ottieni</th>
                <th className="w-32 px-4 py-4 text-center font-medium">Gratuito</th>
                <th className="w-40 bg-slate-200/40 px-4 py-4 text-center font-display text-base dark:bg-slate-800/40">
                  <span className="inline-flex items-center gap-1.5"><Award className="h-4 w-4 text-slate-500" />Silver</span>
                </th>
                <th className="relative w-40 bg-gold-muted/40 px-4 py-4 text-center font-display text-base">
                  <span className="absolute left-1/2 top-0 -translate-x-1/2 -translate-y-1/2 rounded-full bg-gold px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wider text-gold-foreground shadow-sm">
                    Consigliato
                  </span>
                  <span className="inline-flex items-center gap-1.5"><Crown className="h-4 w-4 text-gold" />Gold</span>
                </th>
              </tr>
            </thead>
            <tbody className="divide-y divide-border bg-card">
              {ROWS.map((r) => (
                <tr key={r.feature}>
                  <td className="px-4 py-3 text-sm">{r.feature}</td>
                  <td className="px-4 py-3 text-center"><Cell v={r.free} /></td>
                  <td className="bg-slate-100/40 px-4 py-3 text-center dark:bg-slate-900/30"><Cell v={r.silver} /></td>
                  <td className="bg-gold-muted/20 px-4 py-3 text-center"><Cell v={r.gold} /></td>
                </tr>
              ))}
              <tr className="bg-secondary/40">
                <td className="px-4 py-4 font-medium">Prezzo</td>
                <td className="px-4 py-4 text-center text-sm">Gratis</td>
                <td className="bg-slate-100/40 px-4 py-4 text-center dark:bg-slate-900/30">
                  <div className="font-display text-2xl">€9,99<span className="text-sm text-muted-foreground">/mese</span></div>
                </td>
                <td className="bg-gold-muted/30 px-4 py-4 text-center">
                  <div className="font-display text-2xl">€19,99<span className="text-sm text-muted-foreground">/mese</span></div>
                </td>
              </tr>
            </tbody>
          </table>
        </div>

        {user && chefQ.data === false ? (
          <div className="mt-8 rounded-xl border border-gold/40 bg-gold-muted/20 p-6 text-center">
            <p className="text-sm">
              Completa prima il tuo profilo Chef per accedere ai vantaggi Premium.
            </p>
            <Button asChild size="lg" className="mt-4 bg-gold text-gold-foreground hover:bg-gold/90">
              <Link to="/dashboard/profilo">Completa il profilo</Link>
            </Button>
          </div>
        ) : sub.tier === "gold" ? (
          <div className="mt-8 rounded-xl border border-gold/40 bg-gold-muted/20 p-6 text-center">
            <div className="inline-flex items-center gap-2 rounded-full bg-gold px-3 py-1 text-xs font-semibold uppercase tracking-wider text-gold-foreground">
              <Crown className="h-3.5 w-3.5" /> Piano attuale: Gold
            </div>
            <p className="mt-3 text-sm text-muted-foreground">
              Hai attivo il piano Gold. Stai già beneficiando della massima visibilità su ChefConnect.
            </p>
            <Button asChild size="lg" variant="outline" className="mt-4">
              <Link to="/dashboard/account">Gestisci abbonamento</Link>
            </Button>
          </div>
        ) : sub.tier === "silver" ? (
          <>
            <div className="mt-8 flex flex-col items-center gap-2">
              <div className="inline-flex items-center gap-2 rounded-full bg-slate-200 px-3 py-1 text-xs font-semibold uppercase tracking-wider text-slate-900 dark:bg-slate-800 dark:text-slate-100">
                <Award className="h-3.5 w-3.5" /> Piano attuale: Silver
              </div>
              <Button
                size="lg"
                className="mt-2 bg-gold text-gold-foreground shadow-md ring-2 ring-gold/30 hover:bg-gold/90"
                onClick={() => handleSubscribe("chef_gold_monthly")}
              >
                <Crown className="mr-1.5 h-4 w-4" />
                Passa a Gold — €19,99/mese
              </Button>
            </div>
            <p className="mt-3 text-center text-xs text-muted-foreground">
              Stai pagando €9,99/mese. L'upgrade è immediato e ti garantisce massima visibilità.
            </p>
          </>
        ) : (
          <>
            <div className="mt-8 flex flex-col items-center gap-3 sm:flex-row sm:justify-center">
              <Button
                size="lg"
                variant="outline"
                onClick={() => handleSubscribe("chef_silver_monthly")}
              >
                <Award className="mr-1.5 h-4 w-4 text-slate-500" />
                Attiva Silver — €9,99/mese
              </Button>
              <div className="relative">
                <span className="absolute -top-2.5 left-1/2 -translate-x-1/2 rounded-full bg-gold px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wider text-gold-foreground shadow-sm">
                  Consigliato
                </span>
                <Button
                  size="lg"
                  className="bg-gold text-gold-foreground shadow-md ring-2 ring-gold/30 hover:bg-gold/90"
                  onClick={() => handleSubscribe("chef_gold_monthly")}
                >
                  <Crown className="mr-1.5 h-4 w-4" />
                  Attiva Gold — €19,99/mese
                </Button>
              </div>
            </div>
            <div className="mt-3 text-center text-xs text-muted-foreground">
              Sei tra i primi 100 chef? <Link to="/diventa-chef" className="underline">Ottieni 30 giorni di Silver gratis</Link>
            </div>
            <p className="mt-2 text-center text-xs text-muted-foreground">
              Puoi cambiare piano o annullare in qualsiasi momento.
            </p>
          </>
        )}

        {isOpen && (
          <div className="fixed inset-0 z-50 flex items-start justify-center overflow-y-auto bg-black/60 p-4">
            <div className="relative w-full max-w-2xl rounded-lg bg-card p-4 shadow-2xl">
              <button
                onClick={closeCheckout}
                className="absolute right-3 top-3 rounded-full bg-secondary p-1.5 text-foreground hover:bg-secondary/80"
                aria-label="Chiudi"
              >
                <X className="h-4 w-4" />
              </button>
              {checkoutElement}
            </div>
          </div>
        )}
      </section>

      <section className="border-t border-border bg-secondary/30">
        <div className="container mx-auto max-w-3xl px-4 py-20">
          <h2 className="text-center font-display text-3xl sm:text-4xl">Domande frequenti</h2>
          <Accordion type="single" collapsible className="mt-8">
            {FAQS.map((f, i) => (
              <AccordionItem key={i} value={`f-${i}`}>
                <AccordionTrigger className="text-left">{f.q}</AccordionTrigger>
                <AccordionContent>{f.a}</AccordionContent>
              </AccordionItem>
            ))}
          </Accordion>
        </div>
      </section>
    </>
  );
}